import {
  doc,
  DocumentData,
  getDoc,
  getDocs,
  QueryDocumentSnapshot,
  Timestamp,
} from 'firebase/firestore';
import React from 'react';
import styled from 'styled-components';
import { FilterItem, firestore, getQuery, OrderItem } from 'services';
import toast from 'react-hot-toast';
import { MONTHS } from 'app-constants';
import { WList } from 'shared/Components';
import { useParams } from 'react-router-dom';

import FilterControle from '../PayoutsScreen/Components/FilterControle';
import { Box, Row, Text } from '../../../../shared/Typhography';

const Container = styled.div`
  margin-top: 150px;
`;

interface RevenueBase extends DocumentData {
  artist: number;
  company: number;
  total: number;
  date: Timestamp;
  share: number;
}

interface RevenueRowProps {
  data: QueryDocumentSnapshot<RevenueBase>;
}

const OrderingCarrot = styled.span<{ direction: 'asc' | 'desc' }>`
  display: inline-block;
  transform: rotate(${({ direction }) => (direction == 'asc' ? '90deg' : '-90deg')});
`;

const dateParse = (date: Date) => {
  if (!date) return 'Not Set';

  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

const RevenueRow = ({ data }: RevenueRowProps) => {
  return (
    <Row style={{ height: '64px', padding: '0 10px' }}>
      <span style={{ flex: 1 }}>{dateParse(data?.data().date.toDate())}</span>
      <span style={{ flex: 1 }}>${data.data().artist.toFixed(5)}</span>
      <span style={{ flex: 1 }}>${data.data().total.toFixed(5)}</span>
    </Row>
  );
};

export default function RevenueByArtistScreen() {
  const { id } = useParams<{ id: string }>();

  const [puzzleArtist, setPuzzleArtist] = React.useState<any>();

  const [data, setData] = React.useState<QueryDocumentSnapshot<RevenueBase>[]>([]);
  const [isLoading, setLoading] = React.useState<boolean>(true);
  const [filters, setFilters] = React.useState<{
    firebase: FilterItem[];
    fields: { [key: string]: any };
  }>({ firebase: [], fields: {} });
  const [ordering, setOrdering] = React.useState<OrderItem[]>([]);
  const [total, setTotal] = React.useState<{
    dateRange?: { min: Date; max: Date };
    artist?: number;
    total?: number;
  }>({});

  const calculateTotal = () => {
    try {
      const nextQuery = getQuery(`usersWeb/${id}/revenue`, filters.firebase, []);
      const tTotal = { artist: 0, total: 0 };
      getDocs(nextQuery)
        .then((docs) => {
          docs.docs.forEach((doc) => {
            tTotal.artist += doc.data().artist;
            tTotal.total += doc.data().total;
          });
        })
        .then(() => setTotal(tTotal));
    } catch (err) {
      toast.error('Failed to calculate totals');
    }
  };

  const loadRelatedData = async () => {
    const tArtist = await getDoc(doc(firestore, `usersWeb/${id}`));
    setPuzzleArtist(tArtist);
  };

  const load = async (reset = false) => {
    setLoading(true);
    try {
      const nextQuery = getQuery(
        `usersWeb/${id}/revenue`,
        filters.firebase,
        ordering,
        data.length > 0 && !reset ? data.pop() : undefined,
        20,
      );
      const nextData = await getDocs(nextQuery);
      nextData.docs;
      setData(
        reset
          ? (nextData.docs as QueryDocumentSnapshot<RevenueBase>[])
          : [...data, ...(nextData.docs as QueryDocumentSnapshot<RevenueBase>[])],
      );
    } catch (err) {
      toast.error('Failed to fetch');
      console.log(err);
    }
    setLoading(false);
  };

  const manageOrdering = (attribute: string) => {
    const orderingItem = ordering.find((item) => item.field === attribute);

    if (orderingItem) {
      if (orderingItem.type === 'asc')
        setOrdering(
          ordering.map((item) => (item?.field === attribute ? { ...item, type: 'desc' } : item)),
        );
      else setOrdering(ordering.filter((item) => item?.field !== attribute));

      return;
    }

    setOrdering([...ordering, { field: attribute, type: 'asc' }]);
  };

  /* Initial data load */
  React.useEffect(() => {
    loadRelatedData();
  }, []);

  React.useEffect(() => {
    calculateTotal();
  }, [filters.firebase]);

  React.useEffect(() => {
    load(true);
  }, [filters.firebase, ordering]);

  return (
    <Container>
      <Box padding="0">
        <div style={{ padding: '10px', boxSizing: 'border-box', display: 'flex' }}>
          {puzzleArtist && (
            <div style={{ flex: 1 }}>
              <img
                src={puzzleArtist.data().profileImageUrl}
                style={{ width: '100%', maxWidth: '256px', maxHeight: '256px' }}
              />
              <Text>{puzzleArtist.data().name ?? puzzleArtist.data().Name}</Text>
            </div>
          )}
        </div>
        <div style={{ padding: '10px' }}>
          <Row style={{ height: '64px', padding: '0 10px' }}>
            <span style={{ flex: 1 }}>Date Range</span>
            <span style={{ flex: 1 }}>Artist Royalty</span>
            <span style={{ flex: 1 }}>Total</span>
          </Row>
          <Row style={{ height: '64px', padding: '0 10px' }}>
            {total && (
              <>
                <span style={{ flex: 1 }}>
                  {filters?.fields?.date?.startDate &&
                    filters?.fields?.date?.endDate &&
                    `${dateParse(filters.fields.date.startDate)} - ${dateParse(
                      filters.fields.date.endDate,
                    )}`}
                  {(!filters?.fields?.date?.startDate || !filters?.fields?.date?.endDate) &&
                    'Unset'}
                </span>
                <span style={{ flex: 1 }}>${total.artist?.toFixed(5)}</span>
                <span style={{ flex: 1 }}>${total.total?.toFixed(5)}</span>
              </>
            )}
            {!total && <span style={{ flex: 1 }}>Loading...</span>}
          </Row>
        </div>
        <div style={{ padding: '10px' }}>
          <FilterControle
            loading={isLoading}
            onChange={(item) => {
              setFilters({
                firebase: [
                  ...filters.firebase.filter((filter) => filter?.attribute !== item?.name),
                  { attribute: item?.name, value: item?.value, operator: '==' },
                ],
                fields: {
                  ...filters.fields,
                  [item.name]: item,
                },
              });
            }}
            clear={(name: string) => {
              setFilters({
                firebase: filters.firebase.filter((item: any) => item?.attribute !== name),
                fields: {
                  ...filters.fields,
                  [name]: undefined,
                },
              });
            }}
            values={filters.fields}
            keys={[
              {
                name: 'date',
                placeholder: 'Date Range',
                options: [],
                type: 'date-range',
                onChange: (dates: [Date | null, Date | null]) => {
                  const start = dates['0'];
                  const end = dates['1'];
                  if (start) {
                    start.setUTCHours(0);
                    start.setUTCMinutes(0);
                    start.setUTCSeconds(0);
                  }

                  if (end) {
                    end.setUTCHours(23);
                    end.setUTCMinutes(59);
                    end.setUTCSeconds(59);
                  }

                  if (!start || !end) {
                    if (filters.firebase.find((fil) => fil.attribute === 'date')) {
                      setFilters({
                        firebase: filters.firebase.filter((fil) => fil.attribute !== 'date'),
                        fields: {
                          ...filters.fields,
                          date: undefined,
                        },
                      });
                    }
                    return;
                  }

                  let firebaseFilter: FilterItem[] = filters.firebase;
                  if (start && end) {
                    firebaseFilter.push({
                      value: Timestamp.fromDate(start),
                      attribute: 'date',
                      operator: '>=',
                    });

                    firebaseFilter.push({
                      value: Timestamp.fromDate(end),
                      attribute: 'date',
                      operator: '<=',
                    });
                  } else {
                    firebaseFilter = firebaseFilter.filter((item) => item?.attribute !== 'date');
                  }

                  setFilters({
                    firebase: [...firebaseFilter],
                    fields: {
                      ...filters.fields,
                      date: { startDate: start, endDate: end },
                    },
                  });
                },
              },
            ]}
          />
        </div>
        <Row style={{ height: '64px', padding: '0 10px' }}>
          <span
            style={{ flex: 1, fontWeight: 600, cursor: 'pointer' }}
            onClick={() => manageOrdering('date')}
          >
            Date
            {ordering?.find((oitem) => oitem?.field === 'date') && (
              <OrderingCarrot
                direction={ordering?.find((oitem) => oitem?.field === 'date')?.type ?? 'asc'}
              >
                &#8227;
              </OrderingCarrot>
            )}
          </span>
          <span
            style={{ flex: 1, fontWeight: 600, cursor: 'pointer' }}
            onClick={() => manageOrdering('artist')}
          >
            Artist Royalty
            {ordering?.find((oitem) => oitem?.field === 'artist') && (
              <OrderingCarrot
                direction={ordering?.find((oitem) => oitem?.field === 'artist')?.type ?? 'asc'}
              >
                &#8227;
              </OrderingCarrot>
            )}
          </span>
          <span
            style={{ flex: 1, fontWeight: 600, cursor: 'pointer' }}
            onClick={() => manageOrdering('total')}
          >
            Total
            {ordering?.find((oitem) => oitem?.field === 'total') && (
              <OrderingCarrot
                direction={ordering?.find((oitem) => oitem?.field === 'total')?.type ?? 'asc'}
              >
                &#8227;
              </OrderingCarrot>
            )}
          </span>
          {/* <span  style={{ flex: 1, fontWeight: 600, cursor: 'pointer' }}>
            Details
          </span> */}
        </Row>
        <WList
          data={data}
          maxHeight={window.innerHeight - 300}
          itemHeight={64}
          itemComponent={RevenueRow}
          scrollConfig={{
            fromBottom: 100,
            event: async () => {
              if (!isLoading) load();
            },
          }}
        />
      </Box>
    </Container>
  );
}
